<template>
  <div class="d-flex justify-center align-center h-100">
    <LinkedinDialog
      :dialog="dialog"
      @close="dialog = false"
      :accounts="linkedin_accounts"
      @add="addAccounts"
    />
    <v-card
      style="
        position: relative;
        border: 1px solid hsl(214.3 31.8% 91.4%);
        border-radius: 0.5rem;
        box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
        display: grid;
        overflow: visible;
      "
      width="100%"
      max-width="800"
      variant="flat"
      class="pa-4 bg-smokeDark"
    >
      <div>
        <h1 class="mb-2">Get Started</h1>
      </div>
      <v-stepper
        v-model="step"
        hide-actions
        flat
        density="compact"
        style="font-size: 10pt"
        :items="['Connect LinkedIn', 'Import posts', 'Generate posts']"
      >
        <template #[`item.1`]>
          <div v-if="loading.accounts">
            <v-progress-linear
              class="my-2"
              indeterminate
              color="#18181b"
            ></v-progress-linear>
          </div>
          <div v-else-if="accounts.length === 0">
            <p>Connect your LinkedIn account to start generating posts.</p>
            <v-btn
              v-if="
                !userProfile?.linkedin ||
                userProfile?.linkedin?.organizations?.length === 0
              "
              @click="connectLinkedin"
              :loading="fetching_accounts"
              class="text-none font-weight-medium my-2"
              color="#18181b"
              variant="flat"
              style="border-radius: 6px; letter-spacing: 0"
              prepend-icon="mdi-linkedin"
              >Connect LinkedIn</v-btn
            >
            <v-menu v-if="userProfile?.linkedin?.organizations?.length > 0">
              <template v-slot:activator="{ props }">
                <v-btn
                  class="text-none font-weight-medium my-2"
                  color="#18181b"
                  variant="flat"
                  v-bind="props"
                  >Select account
                  <v-icon size="small" class="ml-2">mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-list>
                  <v-list-item
                    v-for="a in userProfile.linkedin.organizations.sort(
                      (a, b) => a.name.localeCompare(b.name),
                    )"
                    :key="a.id"
                    @click="selectAccount(a)"
                  >
                    {{ a.name }}
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </div>
          <div v-else-if="accounts[0].access_token_valid">
            <p>
              Your LinkedIn account is connected. You can continue to import
              your posts.
            </p>
          </div>
          <div v-else>
            <p>
              Your LinkedIn account is disconnected. Please connect your account
              to continue.
            </p>
            <v-btn
              @click="connectLinkedin"
              class="text-none font-weight-medium my-2"
              color="#18181b"
              variant="flat"
              style="border-radius: 6px; letter-spacing: 0"
              prepend-icon="mdi-linkedin"
              >Connect LinkedIn</v-btn
            >
          </div>
        </template>

        <template #[`item.2`]>
          <div>
            <div
              v-if="
                categories.filter((c) => c.status === 'suggested').length === 0
              "
            >
              <p>Import your posts from LinkedIn to start generating posts.</p>
              <div class="d-flex align-center" style="gap: 12px">
                <v-btn
                  class="text-none font-weight-medium my-2"
                  color="#18181b"
                  variant="flat"
                  @click="generateCategories"
                  :loading="generating_categories"
                  style="border-radius: 6px; letter-spacing: 0"
                  >Import Posts</v-btn
                >
                <Transition name="fade" mode="out-in">
                  <p
                    v-if="generating_categories"
                    :key="reasoningText"
                    style="font-size: 11pt; color: #6b7280; margin: 0"
                  >
                    {{ reasoningText }}
                  </p>
                </Transition>
              </div>
            </div>

            <div
              v-if="
                categories.filter((c) => c.status === 'suggested').length > 0
              "
            >
              <p class="mb-2 font-weight-medium">
                Posts imported successfully. Your posts have been categorized
                into the following categories. Select the categories you want to
                keep.
              </p>
              <v-btn
                class="text-none font-weight-medium my-2"
                color="#18181b"
                size="small"
                variant="flat"
                @click="combineCategories"
                style="border-radius: 6px; letter-spacing: 0"
                :disabled="categories.filter((c) => c.selected).length < 2"
                prepend-icon="mdi-merge"
              >
                Combine categories
              </v-btn>
              <div
                style="max-height: 400px; overflow-y: auto; padding-right: 10px"
              >
                <v-card
                  class="pa-4"
                  style="
                    display: grid;
                    grid-template-columns: 50px 1fr 100px;
                    gap: 10px;
                  "
                  v-for="c in categories.filter(
                    (c) => c.status === 'suggested',
                  )"
                  :key="c.uid"
                >
                  <div class="">
                    <v-checkbox
                      v-model="c.selected"
                      :hide-details="true"
                    ></v-checkbox>
                  </div>
                  <div>
                    <p class="font-weight-medium" style="font-size: 14px">
                      {{ c.name }}
                    </p>
                    <p style="font-size: 12px" class="text-muted">
                      {{ c.description }}
                    </p>
                  </div>
                  <KeepCategory
                    :category="c"
                    @select="c.selected = true"
                    :onboarding="true"
                  />
                </v-card>
              </div>
            </div>
          </div>
        </template>

        <template #[`item.3`]>
          <div>
            <p class="mb-4">
              You're all set! Here are some next steps to get the most out of
              Stix.
            </p>
            <div
              style="display: grid; grid-template-columns: 1fr 1fr; gap: 16px"
            >
              <!-- Posts Card -->
              <div
                style="
                  border: 1px solid #e5e7eb;
                  border-radius: 8px;
                  padding: 20px;
                  background: #fafafa;
                "
              >
                <div class="d-flex align-center mb-3" style="gap: 10px">
                  <div
                    style="
                      background: #18181b;
                      border-radius: 8px;
                      padding: 8px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <v-icon size="20" color="white">mdi-pencil-outline</v-icon>
                  </div>
                  <p style="font-size: 14px; font-weight: 600; margin: 0">
                    Create Content
                  </p>
                </div>
                <p style="font-size: 13px; color: #6b7280; margin-bottom: 16px">
                  Add more posts manually or generate new content with AI to
                  keep your feed fresh and engaging.
                </p>
                <v-btn
                  color="#18181b"
                  variant="flat"
                  block
                  class="text-none font-weight-medium"
                  style="border-radius: 6px; letter-spacing: 0"
                  @click="$router.push('/library')"
                >
                  Go to Posts
                </v-btn>
              </div>

              <!-- Categories Card -->
              <div
                style="
                  border: 1px solid #e5e7eb;
                  border-radius: 8px;
                  padding: 20px;
                  background: #fafafa;
                "
              >
                <div class="d-flex align-center mb-3" style="gap: 10px">
                  <div
                    style="
                      background: #18181b;
                      border-radius: 8px;
                      padding: 8px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                    "
                  >
                    <v-icon size="20" color="white">mdi-tune-variant</v-icon>
                  </div>
                  <p style="font-size: 14px; font-weight: 600; margin: 0">
                    Fine-tune Categories
                  </p>
                </div>
                <p style="font-size: 13px; color: #6b7280; margin-bottom: 16px">
                  Set up schedules, goals, and preferences for each category to
                  optimize your posting strategy.
                </p>
                <v-btn
                  color="#18181b"
                  variant="flat"
                  block
                  class="text-none font-weight-medium"
                  style="border-radius: 6px; letter-spacing: 0"
                  @click="$router.push({ path: '/library', query: { openCategoryManager: 'true' } })"
                >
                  Go to Categories
                </v-btn>
              </div>
            </div>
          </div>
        </template>
      </v-stepper>
      <div class="d-flex justify-space-between align-center mt-4">
        <div>
          <v-btn
            v-if="step > 1"
            @click="step--"
            variant="outlined"
            class="text-none font-weight-medium"
            style="border-radius: 6px; letter-spacing: 0"
          >
            Back
          </v-btn>
        </div>
        <div>
          <v-btn
            v-if="step < 3"
            @click="nextStep"
            :loading="saving_categories"
            color="#18181b"
            variant="flat"
            :disabled="
              step === 1 &&
              accounts.filter((a) => a.access_token_valid).length === 0
            "
            class="text-none font-weight-medium"
            style="border-radius: 6px; letter-spacing: 0"
          >
            Continue
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import LinkedinDialog from "@/components/linkedin/LinkedinDialog.vue";
import { onboarding } from "@/mixins/onboarding";
import { mapState } from "vuex";
import KeepCategory from "@/components/category/KeepCategory.vue";
export default {
  mixins: [onboarding],
  components: {
    LinkedinDialog,
    KeepCategory,
  },
  computed: {
    ...mapState({
      userProfile: (state) => state.users.userProfile,
      accounts: (state) => state.accounts.accounts,
      loading: (state) => state.loading,
    }),
  },
  data() {
    return {
      step: 1,
      reasoningText: "",
      reasoningIndex: 0,
      reasoningInterval: null,
      reasoningSteps: [
        "Fetching all LinkedIn posts...",
        "Reading post content...",
        "Analyzing topics and themes...",
        "Grouping similar posts together...",
        "Categorizing topics...",
        "Generating category descriptions...",
        "Finalizing categories...",
      ],
    };
  },
  watch: {
    generating_categories(val) {
      if (val) {
        this.reasoningIndex = 0;
        this.reasoningText = this.reasoningSteps[0];
        this.reasoningInterval = setInterval(() => {
          this.reasoningIndex++;
          if (this.reasoningIndex >= this.reasoningSteps.length) {
            this.reasoningIndex = this.reasoningSteps.length - 1;
          }
          this.reasoningText = this.reasoningSteps[this.reasoningIndex];
        }, 10000);
      } else {
        clearInterval(this.reasoningInterval);
        this.reasoningText = "";
      }
    },
  },
  beforeUnmount() {
    clearInterval(this.reasoningInterval);
  },
  methods: {
    async nextStep() {
      if (this.step === 2) {
        this.saving_categories = true;
        const suggested = this.categories.filter(
          (c) => c.status === "suggested",
        );
        await Promise.all(
          suggested.map(async (c) => {
            if (c.selected) {
              await this.$store.dispatch("keepCategory", c);
              this.$store.dispatch("generatePosts", {
                count: 2,
                posts: c.based_on.map((p) => p.commentary),
                category_uid: c.uid,
              });
            } else {
              await this.$store.dispatch("deleteCategory", {
                uid: c.uid,
                silent: true,
              });
            }
          }),
        );
        this.saving_categories = false;
      }
      this.step++;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
